// src/components/ProjectBuilder/AirportManagerFilters.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import { TaskFilters as TaskFiltersType } from '../../services/taskApi';
import { listUsers, User } from '../../services/userApi';
import { listProjects, ProjectResponse } from '../../services/projectApi';
import { getAirports, Airport } from '../../services/airportApi';

export interface AirportManagerFiltersProps {
  filters: TaskFiltersType;
  onFilterChange: (filters: TaskFiltersType) => void;
}

const USER_TYPES = ['Tenant', 'Client', 'Fraport'] as const;
type UserType = typeof USER_TYPES[number];

export default function AirportManagerFilters({ filters, onFilterChange }: AirportManagerFiltersProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<ProjectResponse[]>([]);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Parse selected user IDs from filter
  const selectedUserIds = filters.userIds
    ? filters.userIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
    : filters.userId
    ? [filters.userId]
    : [];

  // Parse selected project IDs from filter
  const selectedProjectIds = filters.projectIds
    ? filters.projectIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
    : filters.projectId
    ? [filters.projectId]
    : [];

  // Parse selected user types
  const selectedUserTypes = filters.userType
    ? (filters.userType.split(',').map(t => t.trim()) as UserType[])
    : [];

  // Load users
  const loadUsers = useCallback(async (search?: string) => {
    setLoadingUsers(true);
    try {
      const response = await listUsers({
        page: 1,
        pageSize: 100,
        search: search || undefined,
        isActive: true,
      });
      setUsers(response.users);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Load airports
  const loadAirports = useCallback(async () => {
    try {
      const response = await getAirports();
      setAirports(response.airports);
    } catch (error) {
      console.error('Error loading airports:', error);
      setAirports([]);
    }
  }, []);

  // Load projects
  const loadProjects = useCallback(async (search?: string) => {
    setLoadingProjects(true);
    try {
      const response = await listProjects({
        page: 1,
        pageSize: 100,
        status: undefined, // Get all projects
      });
      // Filter projects by search query if provided
      let filteredProjects = response.projects;
      if (search) {
        const searchLower = search.toLowerCase();
        filteredProjects = response.projects.filter(
          p => {
            const airport = airports.find(a => a.airportId === p.airportId);
            const airportName = airport ? airport.name : '';
            return p.name.toLowerCase().includes(searchLower) ||
                   airportName.toLowerCase().includes(searchLower);
          }
        );
      }
      // Map to ProjectResponse format with default values for missing fields
      const mappedProjects: ProjectResponse[] = filteredProjects.map(p => ({
        ...p,
        status: p.status as ProjectResponse['status'],
        rootSiteId: null,
        rootDriveId: null,
        rootWebUrl: null,
        libraryName: null,
        lastUpdated: p.createdAt, // Use createdAt as fallback
      }));
      setProjects(mappedProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [airports]);

  // Get airport name by ID
  const getAirportName = (airportId: number | null): string => {
    if (!airportId) return '';
    const airport = airports.find(a => a.airportId === airportId);
    return airport ? airport.name : '';
  };

  // Initial load
  useEffect(() => {
    loadUsers();
    loadAirports();
  }, [loadUsers, loadAirports]);

  // Load projects after airports are loaded
  useEffect(() => {
    if (airports.length > 0) {
      loadProjects();
    }
  }, [airports, loadProjects]);

  // Handle user selection change
  const handleUserChange = (_event: React.SyntheticEvent, newValue: User[]) => {
    const userIds = newValue.map(u => u.userId);
    if (userIds.length === 0) {
      onFilterChange({
        ...filters,
        userId: undefined,
        userIds: undefined,
        page: 1,
      });
    } else if (userIds.length === 1) {
      onFilterChange({
        ...filters,
        userId: userIds[0],
        userIds: undefined,
        page: 1,
      });
    } else {
      onFilterChange({
        ...filters,
        userId: undefined,
        userIds: userIds.join(','),
        page: 1,
      });
    }
  };

  // Handle project selection change
  const handleProjectChange = (_event: React.SyntheticEvent, newValue: ProjectResponse[]) => {
    const projectIds = newValue.map(p => p.projectId);
    if (projectIds.length === 0) {
      onFilterChange({
        ...filters,
        projectId: undefined,
        projectIds: undefined,
        page: 1,
      });
    } else if (projectIds.length === 1) {
      onFilterChange({
        ...filters,
        projectId: projectIds[0],
        projectIds: undefined,
        page: 1,
      });
    } else {
      onFilterChange({
        ...filters,
        projectId: undefined,
        projectIds: projectIds.join(','),
        page: 1,
      });
    }
  };

  // Handle user type toggle
  const handleUserTypeChange = (_event: React.MouseEvent<HTMLElement>, newTypes: UserType[]) => {
    if (newTypes.length === 0) {
      onFilterChange({
        ...filters,
        userType: undefined,
        page: 1,
      });
    } else {
      onFilterChange({
        ...filters,
        userType: newTypes.join(','),
        page: 1,
      });
    }
  };

  // Get selected users
  const selectedUsers = users.filter(u => selectedUserIds.includes(u.userId));

  // Get selected projects
  const selectedProjects = projects.filter(p => selectedProjectIds.includes(p.projectId));

  return (
    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
        Manager Filters
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Slice by User */}
        <Autocomplete
          multiple
          options={users}
          value={selectedUsers}
          onChange={handleUserChange}
          onInputChange={(_event, newInputValue) => {
            if (newInputValue.length > 0) {
              loadUsers(newInputValue);
            }
          }}
          getOptionLabel={(option) => `${option.displayName || option.email} (${option.email})`}
          isOptionEqualToValue={(option, value) => option.userId === value.userId}
          loading={loadingUsers}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Slice by User"
              placeholder="Search users..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.userId}
                label={option.displayName || option.email}
                size="small"
              />
            ))
          }
          size="small"
        />

        {/* Slice by Project */}
        <Autocomplete
          multiple
          options={projects}
          value={selectedProjects}
          onChange={handleProjectChange}
          onInputChange={(_event, newInputValue) => {
            if (newInputValue.length > 0) {
              loadProjects(newInputValue);
            }
          }}
          getOptionLabel={(option) => {
            const airportName = getAirportName(option.airportId);
            return `${option.name}${airportName ? ` (${airportName})` : ''}`;
          }}
          isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
          loading={loadingProjects}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Slice by Project"
              placeholder="Search projects..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingProjects ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.projectId}
                label={option.name}
                size="small"
              />
            ))
          }
          size="small"
        />

        {/* Slice by User Type */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Slice by User Type
          </Typography>
          <ToggleButtonGroup
            value={selectedUserTypes}
            onChange={handleUserTypeChange}
            aria-label="user type filter"
            size="small"
            {...({ multiple: true } as any)}
          >
            {USER_TYPES.map((type) => (
              <ToggleButton key={type} value={type} aria-label={type.toLowerCase()}>
                {type}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
    </Paper>
  );
}

